import React, { useEffect, useState, type HTMLAttributes } from "react";
import { useStore } from "@nanostores/react";
import clsx from "clsx";
import { Chip } from "@nextui-org/chip";

import { parseResources, types, type ParseResource, type Resource } from "@module/resource";
import { excludes, type } from "@module/resource/store";
import { useTranslatedRelative, translateGlossaries as ts } from "@i18next/client/utils";

// widget
import ResourcesCard from "@react/widget/ResourcesCard";
import Img from "@react/widget/Img";
import { H2 } from "@react/widget/Headline";
import moment, { timezones, parseISO8601 } from '@module/timezone';
import Button from "@react/widget/Button";
import Section from "@react/structure/Section";
// import { DateTime, Settings, parseISO8601 } from "@module/luxon";

interface Glossaries extends Record<string, string> {
  "Get the eBook": string;
  "Get the Report": string;
  "Get the Checklist": string;
  "Get the Guide": string;
  "Register Now" : string;
  "Watch On-Demand": string;
  "View Event Details": string;
};

interface Props extends HTMLAttributes<'div'> {
  currentSite: Page.Site;
  init: Array<Resource>;
  glossaries: Glossaries;
}

var first = true;

const LatestCards: React.FC<Props> = ({ currentSite, init, glossaries: gs }) => {
  const tr = useTranslatedRelative(currentSite.locale);
  const $type = useStore(type);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState(parseResources(init));
  const [latestFirst = undefined, ...latestOthers] = entries;
  const { eventInformation: [eventInformation = undefined] = [] } = latestFirst ?? {};
  const startTime = parseISO8601(eventInformation?.startTime, currentSite.locale);
  const now = moment();
  useEffect(() => {
    if (!first) {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("site", currentSite.handle);
      types[$type ?? 0]?.section?.forEach(section => params.append("section", section));

      fetch(`/resources/feature.json?${params.toString()}`)
        .then(res => res.json())
        .then((res: Array<Resource>) => {
          setEntries(parseResources(res));
          excludes.set(res.map(e => `${e.id}`));
          setLoading(false);
          return res;
        })
        .catch(reason => {
          console.error(reason);
          setLoading(false);
          return undefined;
        });
    } else {
      excludes.set(entries.map(e => `${e.id}`));
    }
    first = false;
  }, [$type]);

  if (!latestFirst)
    return null;

  return (
    <>
      <Section>
        <ResourcesCard
          key="latest"
          isLoaded={!loading}
          rounded="none"
          className="max-w-sm md:max-w-full mx-auto w-full [&:hover_img]:scale-110 overflow-hidden md:flex-row-reverse gap-8 md:gap-4 md:[&>div:first-child]:w-7/12 [&>div:last-child]:border-0 [&>div:last-child]:p-0 md:[&>div:last-child]:w-5/12 md:[&>div:last-child]:min-h-full hover:!shadow-none !items-center"
          type={latestFirst.type}
          ebookCategory={latestFirst.ebookCategory}
          eventThumbnail={(
            <a href={latestFirst.url} className={clsx("block absolute inset-0 rounded-lg overflow-hidden", latestFirst.type === "eBook" && "bg-gray-50")}>
              {latestFirst?.hero?.image && <Img className={clsx(latestFirst.type === "eBook" ? "h-auto w-auto mx-auto max-h-[85%] max-w-[60%] top-auto" : "w-full h-full object-cover", "transition duration-500 absolute inset-0")}
                src={latestFirst.hero.image}
                width={(latestFirst.type === "eBook") ? 414 : 690}
                height={(latestFirst.type === "eBook") ? 328 : 387}
                fit={(latestFirst.type === "eBook") ? "inside" : "cover"}
              />}
            </a>
          )}
          topics={latestFirst.topics?.map(t => (
            <a key={t.slug} href={tr(`/resources/topic/${t.slug}`)}>
              <Chip size="sm" variant="flat" classNames={{ base: `rounded-md border-1 transition hover:grayscale` }} style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}0f` }}>
                {t.title}
              </Chip>
            </a>
          ))}
          summary={(
            <a href={latestFirst.url}>
              <H2 size="2xl" className="hover:text-primary transition duration-500 line-clamp-3" title={latestFirst.hero?.title}>{latestFirst.hero?.title}</H2>
              {!!latestFirst?.hero?.subtitle && (
                <p className="mt-4">{latestFirst.hero.subtitle}</p>
              )}
              {!!latestFirst?.hero?.description && (
                <div className="mt-4" dangerouslySetInnerHTML={{ __html: latestFirst?.hero?.description }} />
              )}
              {(latestFirst.sectionHandle === "webinars" && eventInformation) ?
                (!!startTime && startTime > now) && <div className="space-y-2 mt-3 text-sm">
                  {!!eventInformation?.customTime ? <div dangerouslySetInnerHTML={{ __html: eventInformation?.customTime }} /> : startTime?.format('dddd, MMMM Do hh:mm A z')}
                </div> :
                latestFirst.sectionHandle === "workshops" && (!!startTime && startTime > now) && <div className="space-y-2 mt-4">{startTime?.format('dddd, MMMM Do hh:mm A z')}</div>
              }
            </a>
          )}
          cta={(() => {
            let isWebinarOrWorkshop = ["webinars", "workshops"].includes(latestFirst.sectionHandle);
            let isUpcoming = !!startTime && startTime > now;
            let buttonText = latestFirst.type === "eBook"
              ? ts(gs, `Get the ${latestFirst.ebookCategory || "eBook"}`)
              : isWebinarOrWorkshop
                ? ts(gs, isUpcoming ? "Register Now" : "Watch On-Demand")
                : ts(gs, "View Event Details");
            return (
              <Button
                className="!px-0 !justify-start !pb-0 mt-4"
                size="lg"
                href={latestFirst.url}
                icon="arrow-right-long"
              >{buttonText}</Button>
            );
          })()}
          tags={latestFirst.tags?.map(t => (
            <Chip key={t.slug} size="sm" variant="faded" classNames={{ base: `rounded-md bg-transparent` }}>{t.title}</Chip>
          ))}
        />
        {!!latestOthers?.length && (
          <div className="grid md:grid-cols-2 gap-8 border-t pt-12 mt-12 md:mt-24 md:pt-24">
            {
              latestOthers?.map((entry, i) => {
                const { eventInformation: [eventInformation] = [] } = entry;
                const startTime = parseISO8601(eventInformation?.startTime, currentSite.locale);
                return (
                  <ResourcesCard
                    isLoaded={!loading}
                    key={`resource-${i}`}
                    href={entry.url}
                    type={entry.type}
                    ebookCategory={entry.ebookCategory}
                    rounded="none"
                    eventThumbnail={(
                      <a href={entry.url} className={clsx("block absolute inset-0 rounded-lg overflow-hidden", entry.type === "eBook" && "bg-gray-50")}>
                        {entry?.hero?.image && (
                          <Img className={clsx(entry.type === "eBook"
                            ? "h-auto w-auto mx-auto max-h-[85%] max-w-[60%] top-auto"
                            : "w-full h-full object-cover",
                            "transition duration-500 absolute inset-0"
                          )}
                            src={entry.hero.image}
                            width={(entry.type === "eBook") ? 350 : 584}
                            height={(entry.type === "eBook") ? 278 : 327}
                            fit={(entry.type === "eBook") ? "inside" : "cover"}
                          />
                        )}
                      </a>
                    )}
                    topics={entry.topics?.map(t => (
                      <a key={t.slug} href={tr(`/resources/topic/${t.slug}`)}>
                        <Chip
                          size="sm"
                          variant="flat"
                          classNames={{
                            base: `rounded-md border-1 transition hover:grayscale`,
                          }}
                          style={{
                            borderColor: t.color,
                            color: t.color,
                            backgroundColor: `${t.color}0f`,
                          }}
                        >
                          {t.title}
                        </Chip>
                      </a>
                    ))}
                    summary={(
                      <a href={entry.url}>
                        <H2
                          size="lg"
                          className="hover:text-primary transition duration-500 line-clamp-2"
                          title={entry.hero?.title}
                        >{entry.hero?.title}</H2>
                      </a>
                    )}
                    date={entry.sectionHandle === "webinars" &&
                      (!!startTime && startTime > now) ? <div className="space-y-2 mt-4">{!!eventInformation?.customTime ? <div dangerouslySetInnerHTML={{ __html: eventInformation?.customTime }} /> : startTime?.format('dddd, MMMM Do hh:mm A z')}</div>
                      :
                      entry.sectionHandle === "workshops" && (!!startTime && startTime > now) && <div className="space-y-2 mt-4">{startTime?.format('dddd, MMMM Do hh:mm A z')}</div>
                    }
                    cta={(() => {
                      let isWebinarOrWorkshop = ["webinars", "workshops"].includes(entry.sectionHandle);
                      let isUpcoming = !!startTime && startTime > now;
                      let buttonText = entry.type === "eBook"
                        ? ts(gs, `Get the ${entry.ebookCategory || "eBook"}`)
                        : isWebinarOrWorkshop
                          ? ts(gs, isUpcoming ? "Register Now" : "Watch On-Demand")
                          : ts(gs, "View Event Details");
                      return (
                        <Button
                          className="!px-0 !justify-start !pb-0 mt-4"
                          size="lg"
                          href={entry.url}
                          icon="arrow-right-long"
                        >{buttonText}</Button>
                      );
                    })()}
                    tags={entry.tags?.map(t => (
                      <Chip
                        key={t.slug}
                        size="sm"
                        variant="faded"
                        classNames={{
                          base: `rounded-md bg-transparent`,
                        }}
                      >
                        {t.title}
                      </Chip>
                    ))}
                    className="max-w-sm md:max-w-full mx-auto w-full [&:hover_img]:scale-110 overflow-hidden hover:shadow-none [&>div]:border-0"
                  />
                )
              })
            }
          </div>
        )}
      </Section>
      <Section py="0"><hr /></Section>
    </>
  );
}

export default LatestCards;